import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Alert types
 */
export type AlertType = 'info' | 'success' | 'warning' | 'error';

/**
 * Props for AlertBanner component
 */
export interface AlertBannerProps {
  /**
   * Alert type determines color and icon
   */
  type: AlertType;

  /**
   * Alert title/heading
   */
  title: string;

  /**
   * Alert message/description
   */
  message: string;

  /**
   * Optional action buttons
   */
  actions?: Array<{
    label: string;
    href?: string;
    onClick?: () => void;
    variant?: 'primary' | 'secondary';
  }>;

  /**
   * Whether the alert can be dismissed
   */
  dismissible?: boolean;

  /**
   * Callback when alert is dismissed
   */
  onDismiss?: () => void;

  /**
   * Custom icon override
   */
  icon?: React.ReactNode;

  /**
   * Custom className
   */
  className?: string;
}

/**
 * Get icon based on alert type
 */
const getIcon = (type: AlertType): React.ReactNode => {
  switch (type) {
    case 'success':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'warning':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    case 'error':
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'info':
    default:
      return (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

/**
 * Get styles based on alert type
 */
const getTypeStyles = (type: AlertType) => {
  const styles = {
    info: {
      container: 'bg-blue-50 border-blue-200',
      icon: 'text-blue-600',
      title: 'text-blue-900',
      message: 'text-blue-800',
      primaryButton: 'bg-blue-600 hover:bg-blue-700 text-white',
      secondaryButton: 'bg-white text-blue-700 border border-blue-300 hover:bg-blue-50',
    },
    success: {
      container: 'bg-green-50 border-green-200',
      icon: 'text-green-600',
      title: 'text-green-900',
      message: 'text-green-800',
      primaryButton: 'bg-green-600 hover:bg-green-700 text-white',
      secondaryButton: 'bg-white text-green-700 border border-green-300 hover:bg-green-50',
    },
    warning: {
      container: 'bg-amber-50 border-amber-200',
      icon: 'text-amber-600',
      title: 'text-amber-900',
      message: 'text-amber-800',
      primaryButton: 'bg-amber-600 hover:bg-amber-700 text-white',
      secondaryButton: 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-50',
    },
    error: {
      container: 'bg-red-50 border-red-200',
      icon: 'text-red-600',
      title: 'text-red-900',
      message: 'text-red-800',
      primaryButton: 'bg-red-600 hover:bg-red-700 text-white',
      secondaryButton: 'bg-white text-red-700 border border-red-300 hover:bg-red-50',
    },
  };
  return styles[type];
};

/**
 * AlertBanner Component
 *
 * Displays important notifications, warnings, or information to users.
 * Supports dismissible alerts, action buttons, and multiple severity levels.
 *
 * @example
 * ```tsx
 * <AlertBanner
 *   type="warning"
 *   title="Plan Limit Approaching"
 *   message="You've used 90% of your monthly request limit."
 *   actions={[
 *     { label: "Upgrade Plan", href: "/subscription", variant: "primary" },
 *     { label: "View Usage", href: "/usage", variant: "secondary" }
 *   ]}
 *   dismissible
 *   onDismiss={() => console.log('dismissed')}
 * />
 * ```
 */
export function AlertBanner({
  type,
  title,
  message,
  actions,
  dismissible = false,
  onDismiss,
  icon,
  className = '',
}: AlertBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const styles = getTypeStyles(type);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  if (isDismissed) {
    return null;
  }

  return (
    <div
      className={`${styles.container} border-l-4 rounded-xl p-5 shadow-sm animate-slide-up ${className}`}
      role="alert"
      data-testid="alert-banner"
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`flex-shrink-0 ${styles.icon}`} data-testid="alert-icon">
          {icon || getIcon(type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className={`text-base font-bold mb-1 ${styles.title}`} data-testid="alert-title">
            {title}
          </h3>
          <p className={`text-sm ${styles.message}`} data-testid="alert-message">
            {message}
          </p>

          {/* Action Buttons */}
          {actions && actions.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3" data-testid="alert-actions">
              {actions.map((action, index) => {
                const buttonClass = action.variant === 'primary' ? styles.primaryButton : styles.secondaryButton;
                const baseClass = `inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 hover:scale-105 shadow-sm ${buttonClass}`;

                if (action.href) {
                  return (
                    <Link
                      key={index}
                      to={action.href}
                      className={baseClass}
                    >
                      {action.label}
                    </Link>
                  );
                }

                return (
                  <button
                    key={index}
                    onClick={action.onClick}
                    className={baseClass}
                  >
                    {action.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Dismiss Button */}
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Dismiss alert"
            data-testid="alert-dismiss"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default AlertBanner;
